// KotReport — list every KOT placed on a date and reprint any ticket.
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import config from '../../config';
import { usePrinterRouting } from './printerService';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const token = sessionStorage.getItem('token');
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function KotReport() {
  const [date, setDate] = useState(todayStr());
  const [kots, setKots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  // Status filter for the KOT list: all | running | completed | cancelled.
  const [statusFilter, setStatusFilter] = useState('all');
  // Centralised printer routing (Department → mapped printer).
  const { resolveKotPrinter } = usePrinterRouting();

  const load = () => {
    if (!company?.id) return;
    setLoading(true);
    fetch(
      `${config.API_BASE_URL}/restaurantorder/kots/${company.id}?date=${date}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((r) => r.json())
      .then((d) => setKots(Array.isArray(d) ? d : []))
      .catch(() => setKots([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Phase 5: WhatsApp a reprinted KOT to the department too (best-effort).
  const waKot = async (kot, dept) => {
    try {
      const d = new Date(kot.createDate);
      await fetch(`${config.API_BASE_URL}/Home/SendOrderWhatsApp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          Department: dept || '',
          CompanyId: company?.id,
          OrderNo: String(kot.orderId ?? kot.kotNumber ?? ''),
          TableNo: kot.tableno || '',
          Date: d.toLocaleDateString('en-GB'),
          Time: d.toLocaleTimeString(),
        }),
      });
    } catch {
      /* best-effort */
    }
  };

  const reprint = async (kot) => {
    const machineId = window.location.hostname;
    const divider = '--------------------------------\n';

    // Reprint must look EXACTLY like the original order-screen KOT
    // (CreateOrder.sendKotToPrinterAPI): the original splits the order into ONE
    // ticket PER DEPARTMENT and routes each to that department's printer. Mirror
    // that here so alignment, spacing, header/footer, columns, the KOT number and
    // the per-kitchen routing are identical. (Order type / per-item notes aren't
    // persisted, so TYPE is derived from the table and NOTES shows "No Notes" —
    // same wording the original prints when an order has no notes.)
    const grouped = {};
    (kot.items || []).forEach((it) => {
      const dept =
        !it.department || it.department.trim() === ''
          ? 'General'
          : it.department;
      grouped[dept] = grouped[dept] || [];
      grouped[dept].push(it);
    });

    const now = new Date(kot.createDate);
    const date = now.toLocaleDateString('en-GB');
    const time = now.toLocaleTimeString();
    // Original defaults TYPE to DINE-IN; a row with a table is dine-in.
    const type = kot.tableno ? 'DINE-IN' : 'PARCEL';

    let allOk = true;
    for (const [dept, deptItems] of Object.entries(grouped)) {
      // Centralised: Department → mapped printer (fallback handled inside).
      const printerName = resolveKotPrinter(dept);
      const itemsText = deptItems
        .map((it) => {
          const name = (it.name || '').substring(0, 26).padEnd(26, ' ');
          const qty = String(it.qty || 0).padStart(4, ' ');
          return `${name}${qty}`;
        })
        .join('\n');

      let data = '\x1B\x40\x1B\x61\x01\x1B\x45\x01';
      data += (company?.name || 'HOTEL') + '\n\x1B\x45\x00';
      if (company?.address1) data += company.address1 + '\n';
      data +=
        divider + '\x1B\x45\x01KITCHEN ORDER TICKET\n\x1B\x45\x00' + divider;
      data += '\x1B\x61\x00';
      data += `KOT   : ${kot.kotNumber}\nDEPT  : ${dept}\nTYPE  : ${type}\nDATE  : ${date}\nTIME  : ${time}\n`;
      data +=
        divider +
        '\x1B\x45\x01ITEM                      QTY\n\x1B\x45\x00' +
        divider;
      data += itemsText + '\n' + divider;
      data += `NOTES : No Notes\n` + divider;
      data +=
        '\x1B\x61\x01\x1B\x45\x01***** THANK YOU *****\n\x1B\x45\x00\n\n\n\n\x1D\x56\x00';

      try {
        const res = await fetch(`${config.API_BASE_URL}/Service/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: 'KOT',
            content: data,
            machineId,
            companyid: company?.id,
            printerName,
          }),
        });
        if (!res.ok) allOk = false;
      } catch {
        allOk = false;
      }
      await waKot(kot, dept);
    }

    if (allOk) {
      setSnack({ open: true, msg: 'KOT sent to kitchen.', sev: 'success' });
    } else {
      setSnack({
        open: true,
        msg: 'Reprint failed. Check printer.',
        sev: 'error',
      });
    }
  };

  // #6: reprint a CANCELLED / EDITED change-KOT for this order. Picks only the
  // cancelled (activestatus=false) or edited (editRamark set) lines, splits by
  // department, and prints the remark under each item — same shape the order
  // screen prints at cancel/edit time.
  const reprintChange = async (kot, mode) => {
    const machineId = window.location.hostname;
    const divider = '--------------------------------\n';
    const items = (kot.items || []).filter((it) =>
      mode === 'CANCELLED'
        ? it.activestatus === false
        : it.editRamark &&
          String(it.editRamark).trim() &&
          it.activestatus !== false,
    );
    if (!items.length) return;
    const grouped = {};
    items.forEach((it) => {
      const dept =
        !it.department || it.department.trim() === ''
          ? 'General'
          : it.department;
      grouped[dept] = grouped[dept] || [];
      grouped[dept].push(it);
    });
    const now = new Date(kot.createDate);
    const d = now.toLocaleDateString('en-GB');
    const t = now.toLocaleTimeString();
    const type = kot.tableno ? 'DINE-IN' : 'PARCEL';
    let allOk = true;
    for (const [dept, deptItems] of Object.entries(grouped)) {
      const printerName = resolveKotPrinter(dept);
      const itemsText = deptItems
        .map((it) => {
          const name = (it.name || '').substring(0, 26).padEnd(26, ' ');
          const qty = String(it.qty || 0).padStart(4, ' ');
          const rmk = mode === 'CANCELLED' ? it.cancelRemark : it.editRamark;
          const note =
            rmk && String(rmk).trim() ? `\n   * ${String(rmk).trim()}` : '';
          return `${name}${qty}${note}`;
        })
        .join('\n');
      let data = '\x1B\x40\x1B\x61\x01\x1B\x45\x01';
      data += (company?.name || 'HOTEL') + '\n\x1B\x45\x00';
      if (company?.address1) data += company.address1 + '\n';
      data += divider + `\x1B\x45\x01KOT - ${mode}\n\x1B\x45\x00` + divider;
      data += '\x1B\x61\x00';
      data += `KOT   : ${kot.kotNumber}\nDEPT  : ${dept}\nTYPE  : ${type}\nDATE  : ${d}\nTIME  : ${t}\n`;
      data +=
        divider +
        '\x1B\x45\x01ITEM                      QTY\n\x1B\x45\x00' +
        divider;
      data += itemsText + '\n' + divider;
      data +=
        '\x1B\x61\x01\x1B\x45\x01***** ' +
        mode +
        ' *****\n\x1B\x45\x00\n\n\n\n\x1D\x56\x00';
      try {
        const res = await fetch(`${config.API_BASE_URL}/Service/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: 'KOT',
            content: data,
            machineId,
            companyid: company?.id,
            printerName,
          }),
        });
        if (!res.ok) allOk = false;
      } catch {
        allOk = false;
      }
      await waKot(kot, dept);
    }
    setSnack({
      open: true,
      msg: allOk ? `${mode} KOT sent to kitchen.` : 'Reprint failed. Check printer.',
      sev: allOk ? 'success' : 'error',
    });
  };

  // ── Status helpers + counts ──────────────────────────────────────────────
  // Older API rows have no `status` field — treat those as "running" so the
  // report keeps working before the backend change is deployed.
  const kotStatus = (k) => (k.status || 'running').toLowerCase();
  const counts = {
    total: kots.length,
    running: kots.filter((k) => kotStatus(k) === 'running').length,
    completed: kots.filter((k) => kotStatus(k) === 'completed').length,
    cancelled: kots.filter((k) => kotStatus(k) === 'cancelled').length,
  };
  const STATUS_META = {
    running: { label: 'Running', color: 'warning' },
    completed: { label: 'Completed', color: 'success' },
    cancelled: { label: 'Cancelled', color: 'error' },
  };
  const summaryCards = [
    { key: 'all', label: 'Total KOT', value: counts.total, color: '#0a2540' },
    { key: 'running', label: 'Running KOT', value: counts.running, color: '#f59e0b' },
    { key: 'completed', label: 'Completed KOT', value: counts.completed, color: '#10b981' },
    { key: 'cancelled', label: 'Cancelled KOT', value: counts.cancelled, color: '#ef4444' },
  ];
  const visibleKots =
    statusFilter === 'all'
      ? kots
      : kots.filter((k) => kotStatus(k) === statusFilter);

  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={2}
        flexWrap='wrap'
        gap={1}>
        <Typography variant='h5' fontWeight={700}>
          KOT Report
        </Typography>
        <Box display='flex' gap={1} alignItems='center'>
          <TextField
            type='date'
            size='small'
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant='contained' onClick={load} disabled={loading}>
            Load
          </Button>
        </Box>
      </Box>

      {/* Summary counts — click a card to filter the list below. */}
      <Grid container spacing={2} mb={2}>
        {summaryCards.map((c) => {
          const active = statusFilter === c.key;
          return (
            <Grid item xs={6} sm={3} key={c.key}>
              <Card
                variant='outlined'
                onClick={() => setStatusFilter(c.key)}
                sx={{
                  borderRadius: 2,
                  cursor: 'pointer',
                  borderWidth: active ? 2 : 1,
                  borderColor: active ? c.color : 'divider',
                  bgcolor: active ? `${c.color}10` : '#fff',
                }}>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant='caption' color='text.secondary'>
                    {c.label}
                  </Typography>
                  <Typography variant='h5' fontWeight={800} sx={{ color: c.color }}>
                    {c.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {loading ? (
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress />
        </Box>
      ) : visibleKots.length === 0 ? (
        <Alert severity='info'>No KOTs found for this filter.</Alert>
      ) : (
        <Grid container spacing={2}>
          {visibleKots.map((kot, i) => {
            const st = STATUS_META[kotStatus(kot)] || STATUS_META.running;
            return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={`${kot.orderId}-${kot.kotNumber}-${i}`}>
              <Card variant='outlined' sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    mb={1}>
                    <Chip
                      label={`KOT #${kot.kotNumber}`}
                      color='primary'
                      size='small'
                    />
                    <Chip label={st.label} color={st.color} size='small' />
                    <Typography variant='caption' color='text.secondary'>
                      {new Date(kot.createDate).toLocaleTimeString()}
                    </Typography>
                  </Box>
                  <Typography variant='body2' fontWeight={600}>
                    Table: {kot.tableno || '-'}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  {(kot.items || []).map((it, j) => {
                    const isCancelled = it.activestatus === false;
                    const isEdited =
                      !isCancelled &&
                      it.editRamark &&
                      String(it.editRamark).trim();
                    return (
                      <Box key={j}>
                        <Box display='flex' justifyContent='space-between'>
                          <Typography
                            variant='body2'
                            sx={{
                              textDecoration: isCancelled
                                ? 'line-through'
                                : 'none',
                              color: isCancelled
                                ? 'error.main'
                                : 'text.primary',
                            }}>
                            {it.name}
                            {isCancelled && (
                              <Chip
                                label='CANCELLED'
                                color='error'
                                size='small'
                                sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }}
                              />
                            )}
                            {isEdited && (
                              <Chip
                                label='EDITED'
                                color='warning'
                                size='small'
                                sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }}
                              />
                            )}
                          </Typography>
                          <Typography variant='body2' fontWeight={700}>
                            × {it.qty}
                          </Typography>
                        </Box>
                        {isCancelled && it.cancelRemark && (
                          <Typography variant='caption' color='error'>
                            ↳ {it.cancelRemark}
                          </Typography>
                        )}
                        {isEdited && (
                          <Typography variant='caption' color='warning.main'>
                            ↳ {it.editRamark}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                  <Button
                    fullWidth
                    size='small'
                    variant='contained'
                    startIcon={<PrintIcon />}
                    sx={{
                      mt: 1.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                      py: 0.9,
                      background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
                      boxShadow: '0 4px 12px rgba(37,99,235,0.30)',
                      '&:hover': {
                        background: 'linear-gradient(135deg,#1d4ed8,#1e40af)',
                        boxShadow: '0 6px 18px rgba(37,99,235,0.40)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s',
                    }}
                    onClick={() => reprint(kot)}>
                    Reprint KOT
                  </Button>
                  {/* #6: reprint a CANCELLED / EDITED change-KOT when this order
                      has any cancelled or edited lines. */}
                  {(kot.items || []).some((it) => it.activestatus === false) && (
                    <Button
                      fullWidth
                      size='small'
                      variant='outlined'
                      color='error'
                      startIcon={<PrintIcon />}
                      sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                      onClick={() => reprintChange(kot, 'CANCELLED')}>
                      Reprint Cancel KOT
                    </Button>
                  )}
                  {(kot.items || []).some(
                    (it) =>
                      it.editRamark &&
                      String(it.editRamark).trim() &&
                      it.activestatus !== false,
                  ) && (
                    <Button
                      fullWidth
                      size='small'
                      variant='outlined'
                      color='warning'
                      startIcon={<PrintIcon />}
                      sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                      onClick={() => reprintChange(kot, 'EDITED')}>
                      Reprint Edit KOT
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
